import { markCompletionUnread } from "../neucleos/threadCompletionReads";
import { scopeProjectRef, scopedThreadKey } from "@cleo/client-runtime/environment";
import type { EnvironmentThreadShell } from "@cleo/client-runtime/state/models";
import {
  type AtomCommandResult,
  isAtomCommandInterrupted,
  settlePromise,
  squashAtomCommandFailure,
} from "@cleo/client-runtime/state/runtime";
import {
  canSnooze,
  effectiveSettled,
  effectiveSnoozed,
  type ChangeRequestStateLike,
} from "@cleo/client-runtime/state/thread-settled";
import type { ScopedThreadRef, ThreadId } from "@cleo/contracts";
import { useCallback } from "react";

import { resolveSnoozePresets, snoozeWakeDescription } from "../components/Sidebar.snooze";
import {
  buildThreadActionMenuItems,
  withSessionPinMenuItems,
  type ThreadActionMenuId,
  type ThreadActionMenuNoun,
} from "../components/threadActionMenu.logic";
import { stackedThreadToast, toastManager } from "../components/ui/toast";
import { threadEnvironment } from "../state/threads";
import { useAtomCommand } from "../state/use-atom-command";
import {
  readEnvironmentSupportsPinning,
  readEnvironmentSupportsSettlement,
  readEnvironmentSupportsSnooze,
  readEnvironmentSupportsTitleRegeneration,
  readThreadShell,
} from "../state/entities";
import { readLocalApi } from "../localApi";
import { showContextMenuFallback } from "../contextMenuFallback";
import { useUiStateStore } from "../uiStateStore";
import { useCopyToClipboard } from "./useCopyToClipboard";
import { useNewThreadHandler } from "./useHandleNewThread";
import { useClientSettings } from "./useSettings";
import { useThreadActions } from "./useThreadActions";
import { useRoomRailPreferences } from "../neucleos/roomRailPreferences";
import { useWorldViewIfSeated } from "../neucleos/worldView";

function failureToast(title: string, error: unknown) {
  toastManager.add(
    stackedThreadToast({
      type: "error",
      title,
      description: error instanceof Error ? error.message : "An error occurred.",
    }),
  );
}

/** Per-open override so ONE hook instance can serve a list of rows
 * (FD-CAN-20: the session rail's chips). Absent fields fall back to the
 * hook input. */
export interface ThreadActionMenuTarget {
  readonly threadRef?: ScopedThreadRef | null;
  readonly projectCwd?: string | null;
  readonly changeRequestState?: ChangeRequestStateLike | null;
  readonly onStartRename?: () => void;
  /** SR-CAN-03: the sibling-birth hand — receives the lineage and the
   * thread shell read at open time. Absent hides the birth items. */
  readonly onSiblingBirth?: (input: {
    readonly lineage: "fresh" | "fork" | "sub";
    readonly thread: EnvironmentThreadShell;
  }) => void;
  /** RM-CAN-10 law 7: the noun the items speak ("session" for a World
   * session's rows; default "thread"). */
  readonly noun?: ThreadActionMenuNoun;
  readonly sessionPin?: { readonly pinned: boolean; readonly onToggle: () => void };
  readonly onShowInRoom?: () => void;
  /** RM-CAN-10 law 8: the retire hand — a World session's one leaving verb.
   * Absent hides the item. */
  readonly onRetire?: (thread: EnvironmentThreadShell) => void;
  /** RM-CAN-14: the new-child hand — a Local conversation's child, named on the
   * Room Rail under its parent. Absent hides the item; a World births
   * sub-sessions through its own items above. */
  readonly onNewChild?: (thread: EnvironmentThreadShell) => void;
  /** RM-CAN-14: the archive hand — a Local conversation's leaving verb on the
   * Room Rail. Absent hides the item; a World session leaves by Retire. */
  readonly onArchive?: (thread: EnvironmentThreadShell) => void;
}

/**
 * The per-thread action menu (pin, settle, snooze, rename, copy, delete…) as
 * a self-contained hook, for surfaces other than the sidebar row — the chat
 * header, and the session rail's chips (FD-CAN-20). Renders through the
 * native context-menu bridge — except when an `accent` rides along: the
 * native OS menu cannot wear the chip's accent border, so accent menus
 * render through the DOM fallback in every build. Dispatches through the
 * same mutations the sidebar uses.
 *
 * Unlike the sidebar, settle and snooze here never navigate away: the caller
 * is acting on the thread they are reading, and ChatView's parked-thread
 * banner already offers the way back.
 */
export function useThreadActionMenu(input: {
  readonly threadRef: ScopedThreadRef | null;
  /** Fallback for "Copy path" when the thread has no worktree. */
  readonly projectCwd: string | null;
  /** PR state feeding auto-settle classification, as resolved by the caller. */
  readonly changeRequestState: ChangeRequestStateLike | null;
  readonly onStartRename: () => void;
  /** FD-CAN-20 ("border should match chip"): when set, the menu renders
   * through the DOM fallback wearing this accent as its border. */
  readonly accent?: string | null;
  /** SR-CAN-03: the sibling-birth hand; absent hides the birth items. */
  readonly onSiblingBirth?: ThreadActionMenuTarget["onSiblingBirth"];
}) {
  const {
    threadRef,
    projectCwd,
    changeRequestState,
    onStartRename,
    accent = null,
    onSiblingBirth,
  } = input;
  const pinWorldState = useWorldViewIfSeated()?.state;
  const { preferences: pinPreferences, togglePin } = useRoomRailPreferences(
    pinWorldState?.kind === "world-ready" ? pinWorldState.worldKey : "",
  );
  const {
    settleThread,
    unsettleThread,
    snoozeThread,
    unsnoozeThread,
    pinThread,
    unpinThread,
    deleteThread,
  } = useThreadActions();
  const updateThreadMetadata = useAtomCommand(threadEnvironment.updateMetadata, {
    reportFailure: false,
  });
  const handleNewThread = useNewThreadHandler();
  const markThreadUnread = useUiStateStore((s) => s.markThreadUnread);
  const autoSettleAfterDays = useClientSettings((s) => s.sidebarAutoSettleAfterDays);
  const confirmThreadDelete = useClientSettings((s) => s.confirmThreadDelete);
  const timestampFormat = useClientSettings((s) => s.timestampFormat);
  const { copyToClipboard: copyPathToClipboard } = useCopyToClipboard<{ path: string }>({
    onCopy: ({ path }) => {
      toastManager.add({ type: "success", title: "Path copied", description: path });
    },
    onError: (error) => failureToast("Failed to copy path", error),
  });
  const { copyToClipboard: copyBranchToClipboard } = useCopyToClipboard<{ branch: string }>({
    target: "branch name",
    onCopy: ({ branch }) => {
      toastManager.add({ type: "success", title: "Branch copied", description: branch });
    },
    onError: (error) => failureToast("Failed to copy branch", error),
  });
  const { copyToClipboard: copyThreadIdToClipboard } = useCopyToClipboard<{ threadId: ThreadId }>({
    onCopy: ({ threadId }) => {
      toastManager.add({ type: "success", title: "Thread ID copied", description: threadId });
    },
    onError: (error) => failureToast("Failed to copy thread ID", error),
  });

  const openMenu = useCallback(
    (position: { x: number; y: number }, target?: ThreadActionMenuTarget) => {
      const ref = target?.threadRef !== undefined ? target.threadRef : threadRef;
      const cwd = target?.projectCwd !== undefined ? target.projectCwd : projectCwd;
      const crState =
        target?.changeRequestState !== undefined ? target.changeRequestState : changeRequestState;
      const startRename = target?.onStartRename ?? onStartRename;
      const siblingBirth = target?.onSiblingBirth ?? onSiblingBirth;
      const noun = target?.noun ?? "thread";
      const retire = target?.onRetire;
      if (ref === null) return;
      void (async () => {
        const api = readLocalApi();
        if (!api) return;
        // Snapshot at open time — the menu is modal, so state read now is
        // what the user is looking at.
        const thread = readThreadShell(ref);
        if (!thread) return;
        const now = new Date();
        const supports = {
          settlement: readEnvironmentSupportsSettlement(ref.environmentId),
          snooze: readEnvironmentSupportsSnooze(ref.environmentId),
          pinning: readEnvironmentSupportsPinning(ref.environmentId),
          titleRegeneration: readEnvironmentSupportsTitleRegeneration(ref.environmentId),
        };
        const isRegeneratingTitle = thread.titleRegeneration != null;
        const snoozePresets = resolveSnoozePresets(now, timestampFormat);
        const baseItems = buildThreadActionMenuItems({
          branch: thread.branch ?? null,
          isPinned: thread.pinnedAt != null,
          isSettled:
            supports.settlement &&
            effectiveSettled(thread, {
              // Minute-quantized like useNowMinute, so this classification
              // can never disagree with the sidebar partition or ChatView's
              // parked-thread banner within the same minute.
              now: `${now.toISOString().slice(0, 16)}:00.000Z`,
              autoSettleAfterDays,
              changeRequestState: crState,
            }),
          isSnoozed: supports.snooze && effectiveSnoozed(thread, { now: now.toISOString() }),
          canSnoozeNow: canSnooze(thread, { now: now.toISOString() }),
          isRegeneratingTitle,
          supports,
          snoozePresets,
          // SR-CAN-03: birth items only for World-slice-bound threads with a
          // wired hand — never dead items on Local or unbound threads.
          worldBound:
            siblingBirth !== undefined && thread.worldKey != null && thread.sessionId != null,
          noun,
          canRetire: retire !== undefined && thread.worldKey != null && thread.sessionId != null,
        });
        const session =
          pinWorldState?.kind === "world-ready" && thread.worldKey === pinWorldState.worldKey
            ? pinWorldState.index.sessions.find(
                (entry) =>
                  entry.sessionId === thread.sessionId &&
                  entry.lifecycle !== "retired" &&
                  pinWorldState.index.rooms.some(
                    (room) => room.roomId === entry.roomId && !room.archived,
                  ),
              )
            : undefined;
        const sessionPin =
          target?.sessionPin ??
          (session
            ? {
                pinned: pinPreferences.pinnedSessionIds.includes(session.sessionId),
                onToggle: () => togglePin(session.sessionId),
              }
            : undefined);
        const pinItems = sessionPin
          ? withSessionPinMenuItems(baseItems, sessionPin.pinned)
          : thread.worldKey != null && thread.sessionId != null
            ? baseItems.filter((item) => item.id !== "pin" && item.id !== "unpin")
            : baseItems;
        const shownItems = target?.onShowInRoom
          ? [
              ...pinItems.slice(0, 1),
              { id: "show-in-room", label: "Show in room", icon: "folder-tree" },
              ...pinItems.slice(1),
            ]
          : pinItems;
        // The archive hand stands just above Delete, the other leaving verb.
        const deleteAt = shownItems.findIndex((item) => item.id === "delete");
        const withArchive =
          target?.onArchive && thread.worldKey == null && deleteAt >= 0
            ? [
                ...shownItems.slice(0, deleteAt),
                { id: "archive", label: `Archive ${noun}` },
                ...shownItems.slice(deleteAt),
              ]
            : shownItems;
        // The new-child hand leads, where a World's birth items stand.
        const items =
          target?.onNewChild && thread.worldKey == null
            ? [{ id: "new-child", label: "New child" }, ...withArchive]
            : withArchive;
        const clicked = await settlePromise(() =>
          accent === null
            ? api.contextMenu.show(items, position)
            : showContextMenuFallback(items, position, { accent }),
        );
        if (clicked._tag === "Failure" || clicked.value === null) return;
        if (clicked.value === "session-pin") {
          sessionPin?.onToggle();
          return;
        }
        if (clicked.value === "show-in-room") {
          target?.onShowInRoom?.();
          return;
        }
        if (clicked.value === "new-child") {
          target?.onNewChild?.(thread);
          return;
        }
        if (clicked.value === "archive") {
          target?.onArchive?.(thread);
          return;
        }
        const action = clicked.value as ThreadActionMenuId;
        if (action.startsWith("snooze:")) {
          const preset = snoozePresets.find((candidate) => `snooze:${candidate.id}` === action);
          if (!preset) return;
          const result = await snoozeThread(ref, preset.snoozedUntil);
          if (result._tag === "Failure") {
            if (!isAtomCommandInterrupted(result)) {
              failureToast("Failed to snooze thread", squashAtomCommandFailure(result));
            }
            return;
          }
          toastManager.add(
            stackedThreadToast({
              type: "success",
              title: `Snoozed until ${snoozeWakeDescription(preset.snoozedUntil, new Date(), timestampFormat)}`,
              timeout: 5_000,
              actionProps: {
                children: "Undo",
                onClick: () => {
                  void unsnoozeThread(ref).then((undone) => {
                    if (undone._tag === "Failure" && !isAtomCommandInterrupted(undone)) {
                      failureToast("Failed to wake thread", squashAtomCommandFailure(undone));
                    }
                  });
                },
              },
            }),
          );
          return;
        }
        const reportFailure = async (
          title: string,
          run: () => Promise<AtomCommandResult<unknown, unknown>>,
        ) => {
          const result = await run();
          if (result._tag === "Failure" && !isAtomCommandInterrupted(result)) {
            failureToast(title, squashAtomCommandFailure(result));
          }
        };
        switch (action) {
          case "new-thread-on-branch": {
            // Explicit branch carry-over: reuse the thread's worktree when it
            // has one, otherwise its branch on the local checkout.
            const result = await settlePromise(() =>
              handleNewThread(scopeProjectRef(ref.environmentId, thread.projectId), {
                branch: thread.branch,
                worktreePath: thread.worktreePath,
                envMode: thread.worktreePath ? "worktree" : "local",
                startFromOrigin: false,
              }),
            );
            if (result._tag === "Failure") {
              failureToast("Could not create thread", squashAtomCommandFailure(result));
            }
            return;
          }
          case "new-thread":
            siblingBirth?.({ lineage: "fresh", thread });
            return;
          case "new-sub-session":
            siblingBirth?.({ lineage: "sub", thread });
            return;
          case "fork-session":
            siblingBirth?.({ lineage: "fork", thread });
            return;
          case "retire":
            retire?.(thread);
            return;
          case "settle":
            await reportFailure("Failed to settle thread", () => settleThread(ref));
            return;
          case "unsettle":
            await reportFailure("Failed to un-settle thread", () => unsettleThread(ref));
            return;
          case "unsnooze":
            await reportFailure("Failed to wake thread", () => unsnoozeThread(ref));
            return;
          case "pin":
            await reportFailure("Failed to pin thread", () => pinThread(ref));
            return;
          case "unpin":
            await reportFailure("Failed to unpin thread", () => unpinThread(ref));
            return;
          case "rename":
            startRename();
            return;
          case "regenerate-title":
            if (isRegeneratingTitle) return;
            await reportFailure("Failed to regenerate thread title", () =>
              updateThreadMetadata({
                environmentId: ref.environmentId,
                input: { threadId: ref.threadId, regenerateTitle: true },
              }),
            );
            return;
          case "mark-unread":
            markThreadUnread(scopedThreadKey(ref), thread.latestTurn?.completedAt);
            markCompletionUnread(
              thread.worldKey,
              scopedThreadKey(ref),
              thread.latestTurn?.completedAt,
            );
            return;
          case "copy-path": {
            const workspacePath = thread.worktreePath ?? cwd;
            if (!workspacePath) {
              toastManager.add(
                stackedThreadToast({
                  type: "error",
                  title: "Path unavailable",
                  description: "This thread does not have a workspace path to copy.",
                }),
              );
              return;
            }
            copyPathToClipboard(workspacePath, { path: workspacePath });
            return;
          }
          case "copy-branch":
            if (thread.branch) {
              copyBranchToClipboard(thread.branch, { branch: thread.branch });
            }
            return;
          case "copy-thread-id":
            copyThreadIdToClipboard(thread.id, { threadId: thread.id });
            return;
          case "delete": {
            if (confirmThreadDelete) {
              const confirmed = await settlePromise(() =>
                api.dialogs.confirm(
                  [
                    `Delete thread "${thread.title}"?`,
                    "This permanently clears conversation history for this thread.",
                  ].join("\n"),
                  { variant: "destructive" },
                ),
              );
              if (confirmed._tag === "Failure" || !confirmed.value) return;
            }
            const deleted = await deleteThread(ref);
            if (
              deleted._tag === "Failure" &&
              !isAtomCommandInterrupted(deleted) &&
              // A failure with the thread already gone is worktree cleanup
              // failing after a successful delete — deleteThread has toasted
              // that itself, and "Failed to delete thread" would be a lie.
              readThreadShell(ref) !== null
            ) {
              failureToast("Failed to delete thread", squashAtomCommandFailure(deleted));
            }
            return;
          }
          default:
            return;
        }
      })();
    },
    [
      accent,
      autoSettleAfterDays,
      changeRequestState,
      confirmThreadDelete,
      copyBranchToClipboard,
      copyPathToClipboard,
      copyThreadIdToClipboard,
      deleteThread,
      handleNewThread,
      markThreadUnread,
      onSiblingBirth,
      onStartRename,
      pinWorldState,
      pinPreferences.pinnedSessionIds,
      togglePin,
      pinThread,
      projectCwd,
      settleThread,
      snoozeThread,
      threadRef,
      timestampFormat,
      unpinThread,
      unsettleThread,
      unsnoozeThread,
      updateThreadMetadata,
    ],
  );

  return { openMenu };
}
